import React from 'react';
import { Carousel } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Slider.css';
import CTAButton from '../CTAButton/CTAButton';
import CTAPrimaryButton from '../CTAButton/CTAPrimaryButton/CTAPrimaryButton';
import CTASecondaryButton from '../CTAButton/CTASecondaryButton/CTASecondaryButton';
import { useNavigate } from 'react-router-dom';

const isVideo = (url) => {
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
    return videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
};

const isImage = (url) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.heic', '.heif', '.tif', '.tiff'];
    return imageExtensions.some(ext => url.toLowerCase().endsWith(ext));
};

const getVideoType = (url) => {
    const extension = url.split('.').pop().toLowerCase();
    switch (extension) {
        case 'mp4':
            return 'mp4';
        case 'webm':
            return 'webm';
        case 'ogg':
            return 'ogg';
        case 'mov':
            return 'quicktime';
        default:
            return 'mp4';
    }
};

const getAutoplayEmbedUrl = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
    let url;
    try {
        url = new URL(rawUrl);
    } catch (_) {
        return rawUrl;
    }

    const hostname = url.hostname.replace(/^www\./, '').toLowerCase();

    const toYouTubeEmbed = () => {
        let videoId = '';
        if (hostname === 'youtu.be') {
            videoId = url.pathname.split('/').filter(Boolean)[0] || '';
        } else if (hostname.endsWith('youtube.com')) {
            if (url.pathname.startsWith('/watch')) {
                videoId = url.searchParams.get('v') || '';
            } else if (url.pathname.startsWith('/shorts/')) {
                const parts = url.pathname.split('/').filter(Boolean);
                videoId = parts[1] || '';
            } else if (url.pathname.startsWith('/embed/')) {
                videoId = url.pathname.split('/').filter(Boolean)[1] || '';
            }
        }
        if (!videoId) return null;
        const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
        embed.searchParams.set('autoplay', '1');
        embed.searchParams.set('mute', '1');
        embed.searchParams.set('playsinline', '1');
        embed.searchParams.set('rel', '0');
        embed.searchParams.set('modestbranding', '1');
        embed.searchParams.set('controls', '0');
        return embed.toString();
    };

    const toVimeoEmbed = () => {
        let videoId = '';
        if (hostname === 'player.vimeo.com' && url.pathname.startsWith('/video/')) {
            videoId = url.pathname.split('/').filter(Boolean)[1] || '';
        } else if (hostname === 'vimeo.com') {
            const parts = url.pathname.split('/').filter(Boolean);
            const nums = parts.filter((p) => /^\d+$/.test(p));
            videoId = nums.length ? nums[nums.length - 1] : '';
        }
        if (!videoId) return null;
        const embed = new URL(`https://player.vimeo.com/video/${videoId}`);
        embed.searchParams.set('autoplay', '1');
        embed.searchParams.set('muted', '1');
        embed.searchParams.set('title', '0');
        embed.searchParams.set('byline', '0');
        embed.searchParams.set('portrait', '0');
        return embed.toString();
    };

    const toFacebookEmbed = () => {
        // If it's already a plugins URL, reuse and set autoplay
        if (hostname.includes('facebook.com') && url.pathname.startsWith('/plugins/video.php')) {
            const cloned = new URL(url.toString());
            cloned.searchParams.set('autoplay', '1');
            cloned.searchParams.set('show_text', '0');
            return cloned.toString();
        }
        const embed = new URL('https://www.facebook.com/plugins/video.php');
        embed.searchParams.set('href', rawUrl);
        embed.searchParams.set('show_text', '0');
        embed.searchParams.set('autoplay', '1');
        return embed.toString();
    };

    if (hostname === 'youtu.be' || hostname.endsWith('youtube.com')) {
        return toYouTubeEmbed() || rawUrl;
    }
    if (hostname === 'vimeo.com' || hostname === 'player.vimeo.com') {
        return toVimeoEmbed() || rawUrl;
    }
    if (hostname === 'facebook.com' || hostname === 'm.facebook.com' || hostname === 'fb.watch') {
        return toFacebookEmbed() || rawUrl;
    }

    const generic = new URL(url.toString());
    if (!generic.searchParams.has('autoplay')) generic.searchParams.set('autoplay', '1');
    if (!generic.searchParams.has('muted')) generic.searchParams.set('muted', '0');
    return generic.toString();
};

const Slider = (props) => {
    const { slides, style, animation, containerType } = props;
    const navigate = useNavigate();
    console.log("slides",slides);
    const shouldAnimate = animation && animation.animationType && animation.animationType !== 'none';
    return (
        <div
        className={`${containerType === 'container-fluid' ? '' : 'container mt-2'} `}
            {...(shouldAnimate ? {
                'data-aos': animation.animationType,
                'data-aos-duration': Number(animation.animationDuration),
                'data-aos-delay': Number(animation.animationDelay),
            } : {})}
        >
        <Carousel fade>
            {slides?.map((slide) => {
                const mediaUrl = slide.media || slide.image;
                let mediaType = slide.mediaType;
                if (!mediaType) {
                    if (mediaUrl && isVideo(mediaUrl)) mediaType = 'video';
                    else if (mediaUrl && isImage(mediaUrl)) mediaType = 'image';
                    else mediaType = 'embed';
                }
                
                return (
                    <Carousel.Item key={slide.id}>
                        {mediaUrl && (
                            mediaType === 'video' ? (
                                <video
                                    className="d-block w-100 HeightMedium"
                                    autoPlay
                                    muted
                                    loop
                                >
                                    <source src={mediaUrl} type={`video/${getVideoType(mediaUrl)}`} />
                                    Your browser does not support the video tag.
                                </video>
                            ) : mediaType === 'image' ? (
                                <img
                                    className="d-block w-100 HeightMedium"
                                    src={mediaUrl}
                                    alt={slide.title}
                                />
                            ) : (
                                <iframe
                                    className="d-block w-100 HeightMedium"
                                    src={getAutoplayEmbedUrl(mediaUrl)}
                                    style={{ border: 'none' }}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    title={`slide-embed-${slide.id}`}
                                />
                            )
                        )}
                        {mediaType !== 'embed' && (
                            <Carousel.Caption className='container'>
                                <div className="container h-100">
                                    <div className="col-lg-6 col-md-6 col-12 h-100 SliderCaptionCol">
                                        <h3 
                                            className='SlideTitle'
                                            style={{ color: style?.SlideTitle?.color }}
                                        >
                                            {slide.title}
                                        </h3>
                                        <p 
                                            className='SlideCaption'
                                            style={{ color: style?.SlideCaption?.color }}
                                        >
                                            {slide.caption}
                                        </p>
                                        
                                        <div className="d-flex gap-2">

                                            {slide.buttons?.map((btn) => (
                                               btn.button.buttonType === 'primary' ? 
                                               <CTAPrimaryButton
                                                text={btn.button.text}
                                                type={btn.button.type}
                                                link={btn.button.link}
                                                 className={btn.button.className}
                                                style={btn.button.style}
                                                disabled={false}
                                                onClick={() => {
                                                    navigate(btn.button.link);
                                                }}
                                                />
                                                :
                                                <CTASecondaryButton
                                                text={btn.button.text}
                                                type={btn.button.type}
                                                link={btn.button.link}
                                                className={btn.button.className}
                                                style={btn.button.style}
                                                disabled={false}
                                                onClick={() => {
                                                    navigate(btn.button.link);
                                                }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Carousel.Caption>
                        )}
                    </Carousel.Item>
                );
            })}
        </Carousel>
        </div>
    );
};

export default Slider;